/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { ComponentTypeBadge } from 'apps/component-service/src/Screens/AllComponents/elements/ComponentTypeBadge/ComponentTypeBadge';
import { sortVersions } from 'apps/component-service/src/Utilities/SortVersions/SortVersions';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import React, { KeyboardEvent, MouseEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { truncateDisplayString } from '../../../../../../../src/Utilities/TruncateDisplayString/TruncateDisplayString';
import { MaterialIcon } from '../../../../../../Components/MaterialIcon/MaterialIcon';
import { EntityComponentSetComponents, EntityComponentSetItem } from '../../../../../../Services/component';
import { getComponentDetailsWithinSetPath } from '../../../../../../Utilities/GetComponentDetailsWithinSetPath/GetComponentDetailsWithinSetPath';
import { COMPONENT_SET_DETAILS_CONSTANTS } from '../../../../ComponentSetDetails.constants';

/**
 * Component set details components detailed state.
 * @param {EntityComponentSetItem} props Component set details props.
 * @export
 * @returns {JSX.Element} The `<ComponentsDetailedState />` component.
 */
export const ComponentsDetailedState = (props: EntityComponentSetItem): JSX.Element => {
  const { components, webPath } = props;
  const navigate = useNavigate();

  // We want to display the details for the most relevant component
  const getLatestVersion = (component: string): EntityComponentSetComponents => {
    // Sort component versions by semantic version number high to low
    const componentVersions = sortVersions(
      components[component].map((version) => version.version),
      'desc',
    );

    // Get the highest version where editable is true otherwise return the highest renderable version
    const editableVersions = componentVersions.filter((version) => {
      const componentVersion = components[component].find((v) => v.version === version);
      return componentVersion ? componentVersion.editable === true : false;
    });

    const highestEditableVersion = editableVersions[0] ?? null;
    const highestComponentVersion = componentVersions[0];

    if (editableVersions.length > 0 && !!highestEditableVersion) {
      return components[component].find((v) => v.version === highestEditableVersion) as EntityComponentSetComponents;
    } else {
      return components[component].find((v) => v.version === highestComponentVersion) as EntityComponentSetComponents;
    }
  };

  // Create the component details within set path using component name and current set webpath.
  const createComponentPath = useCallback(
    (componentName: string): string => {
      const [namespace, name] = componentName.split('/');
      const currentScreenPath = getCapabilityPath(`/component-sets`);
      const componentDetailsPath = `${currentScreenPath}${getComponentDetailsWithinSetPath(name, namespace, webPath)}`;
      return componentDetailsPath;
    },
    [webPath],
  );

  // Navigate to the component details within set context screen.
  const navigateToComponentDetails = useCallback(
    (componentName: string): void => {
      const componentDetailsPath = createComponentPath(componentName);
      navigate({ pathname: componentDetailsPath });
    },
    [createComponentPath, navigate],
  );

  const goToComponentDetailsMouseHandler = (event: MouseEvent<HTMLButtonElement>, component: string): void => {
    navigateToComponentDetails(component);
  };

  const goToComponentDetailsKeyboardHandler = (event: KeyboardEvent<HTMLButtonElement>, component: string): void => {
    // Keys which should navigate user to the segment.
    const navigateKeys = ['Enter', 'Space'];
    if (navigateKeys.includes(event.code)) {
      navigateToComponentDetails(component);
    }
  };

  // Map over the component set array and look for components
  const componentItems: Array<JSX.Element> = Object.keys(components).map((component: string, index: number) => {
    const latestComponent = getLatestVersion(component);
    const icon = latestComponent.icon;
    return (
      <div className="component-set-details__item" key={index}>
        <button
          className="component-set-details__link"
          onClick={(event): void => goToComponentDetailsMouseHandler(event, component)}
          onKeyDown={(event): void => goToComponentDetailsKeyboardHandler(event, component)}
        >
          <div className="component-set-details__card">
            <div className="component-set-details__body">
              {/* Component Title & Path */}
              <div className="component-set-details__header">
                <div className="component-set-details__icon">
                  {icon ? <MaterialIcon color={icon.color.value} name={icon.id} /> : <MaterialIcon />}
                </div>
                <div className="component-set-details__header-text">
                  <span className="component-set-details__title">
                    {truncateDisplayString(
                      latestComponent.displayName,
                      COMPONENT_SET_DETAILS_CONSTANTS.maxDisplayNameLength,
                    )}
                  </span>
                  <span className="component-set-details__subtitle">{component}</span>
                </div>
              </div>

              {/* Component Contents */}
              <div className="component-set-details__content">
                <span className="component-set-details__description">
                  {truncateDisplayString(
                    latestComponent.description,
                    COMPONENT_SET_DETAILS_CONSTANTS.maxDescriptionLength,
                  )}
                </span>
              </div>

              <div className="component-set-details__footer">
                <div className="component-set-details__stats">
                  <ComponentTypeBadge componentType={latestComponent.type} />
                </div>
              </div>
            </div>
          </div>
        </button>
      </div>
    );
  });

  return (
    <div className="component-set-details__components-container-detailed-state">
      <div className="component-set-details__list">{componentItems}</div>
    </div>
  );
};
